import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Set up axios defaults
axios.defaults.headers.common['Content-Type'] = 'application/json';

// Authentication Context
const AuthContext = React.createContext();

// Auth Provider Component
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userType = localStorage.getItem('userType');
    
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      if (userType === 'admin') {
        setAdmin({ token });
      } else {
        fetchUserProfile();
      }
    }
    setLoading(false);
  }, []);

  const fetchUserProfile = async () => {
    try {
      const response = await axios.get(`${API}/profile`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
      logout();
    }
  };

  const login = async (email, password, isAdmin = false) => {
    try {
      const endpoint = isAdmin ? `${API}/admin/login` : `${API}/login`;
      const response = await axios.post(endpoint, { email, password });
      const { access_token } = response.data;
      
      localStorage.setItem('token', access_token);
      localStorage.setItem('userType', isAdmin ? 'admin' : 'user');
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      if (isAdmin) {
        setAdmin({ token: access_token });
      } else {
        await fetchUserProfile();
      }
      
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Login failed' };
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post(`${API}/register`, userData);
      const { access_token } = response.data;
      
      localStorage.setItem('token', access_token);
      localStorage.setItem('userType', 'user');
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      await fetchUserProfile();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Registration failed' };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userType');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setAdmin(null);
  };

  return (
    <AuthContext.Provider value={{ user, admin, login, register, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook for using auth context
const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Login Component (User Only)
const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showRegister, setShowRegister] = useState(false);
  
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    const result = await login(email, password, false); // Always user login
    if (!result.success) {
      setError(result.error);
    }
    setLoading(false);
  };

  if (showRegister) {
    return <Register onBackToLogin={() => setShowRegister(false)} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-2xl shadow-xl">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">🏋️ Studio Partner Finder</h1>
          <h2 className="text-lg text-gray-600">Find Strategic Business Partners</h2>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Enter your email"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Enter your password"
              />
            </div>
          </div>
          
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 px-4 rounded-lg transition duration-200 disabled:opacity-50"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
          
          <div className="text-center">
            <p className="text-sm text-gray-600">
              Don't have an account?{' '}
              <button
                type="button"
                onClick={() => setShowRegister(true)}
                className="text-indigo-600 hover:text-indigo-500 font-medium"
              >
                Create free account
              </button>
            </p>
          </div>
        </form>

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <div className="text-center space-y-3">
            <p className="text-sm font-medium text-gray-900">
              Ready to take your studio to the next level?
            </p>
            <p className="text-xs text-gray-600">
              Easily run ads, automate follow-up, and fill your studio without an agency.
            </p>
            <div className="flex flex-col gap-2">
              <a 
                href="https://gymmembermagnet.com/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-block bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition duration-200"
              >
                Gym Owners Click Here
              </a>
              <a 
                href="https://studentmagnet.com/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-block bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition duration-200"
              >
                Martial Arts Click Here
              </a>
            </div>
            <p className="text-xs text-gray-500">
              Powered by The Follow Up Ninja™
            </p>
            <p className="text-xs text-gray-400">
              <a href="/privacy-policy" className="hover:text-gray-600 transition-colors">
                Privacy Policy
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Admin Login Component (Separate)
const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    const result = await login(email, password, true); // Always admin login
    if (!result.success) {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-2xl shadow-xl border-2 border-red-200">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">🔐 Admin Access</h1>
          <h2 className="text-lg text-gray-600">Studio Partner Finder Management</h2>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Admin Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="Enter admin email"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Admin Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="Enter admin password"
              />
            </div>
          </div>
          
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition duration-200 disabled:opacity-50"
          >
            {loading ? 'Authenticating...' : 'Admin Login'}
          </button>
          
          <div className="text-center">
            <p className="text-xs text-gray-500">
              Authorized personnel only
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

// Registration Component
const Register = ({ onBackToLogin }) => {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    marketing_consent: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const { register } = useAuth();

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }
    
    // Validate consent
    if (!formData.marketing_consent) {
      setError('You must agree to the marketing communications to continue');
      setLoading(false);
      return;
    }
    
    // Transform data for backend (keeping original structure for compatibility)
    const { confirmPassword, marketing_consent, ...registerData } = formData;
    const backendData = {
      ...registerData,
      email_consent: marketing_consent,
      sms_consent: marketing_consent
    };
    
    const result = await register(backendData);
    
    if (!result.success) {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-2xl shadow-xl">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Create Free Account</h1>
          <p className="text-gray-600">Join thousands of studio owners finding partners</p>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
              <input
                type="text"
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Enter your full name"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Enter your email"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Enter your phone number"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                minLength={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Create a password (min 6 characters)"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
              <input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Confirm your password"
              />
            </div>
            
            <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
              <p className="text-sm font-medium text-gray-700">Marketing Communications Consent:</p>
              <div className="flex items-start">
                <input
                  id="marketing-consent"
                  name="marketing_consent"
                  type="checkbox"
                  checked={formData.marketing_consent}
                  onChange={handleChange}
                  className="mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="marketing-consent" className="ml-2 block text-sm text-gray-700">
                  I agree to receive promotional emails, text messages, and phone calls from The Follow Up Ninja regarding studio partnership opportunities, business growth tips, and related services. I understand that message and data rates may apply, and I can opt out at any time by replying STOP to text messages, unsubscribing from emails, or contacting jon@thefollowupninja.com. This consent is not required as a condition of purchase.
                </label>
              </div>
            </div>
          </div>
          
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 px-4 rounded-lg transition duration-200 disabled:opacity-50"
          >
            {loading ? 'Creating Account...' : 'Create Free Account'}
          </button>
          
          <div className="text-center">
            <button
              type="button"
              onClick={onBackToLogin}
              className="text-indigo-600 hover:text-indigo-500 font-medium text-sm"
            >
              Already have an account? Sign in
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Enhanced Dashboard Component
const Dashboard = () => {
  const [activeTab, setActiveTab] = useState('search');
  const [searchParams, setSearchParams] = useState({
    gym_name: '',
    gym_address: '',
    radius: 5,
    gym_type: '',
    demographics: '',
    offers_to_trade: [],
    partnership_types: [],
    partner_categories: [],
    excluded_categories: [],
    ignore_chains: false
  });
  const [results, setResults] = useState(null);
  const [savedSearches, setSavedSearches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [editingNotes, setEditingNotes] = useState({});
  const [userSettings, setUserSettings] = useState(null);
  const [partnershipModal, setPartnershipModal] = useState({ business: null, show: false });
  const [notesModal, setNotesModal] = useState({ business: null, show: false });
  const [deleteModal, setDeleteModal] = useState({ show: false, searchId: null, searchName: '' });
  const [renameModal, setRenameModal] = useState({ show: false, searchId: null, currentName: '', newName: '' });
  const [scoreModal, setScoreModal] = useState({ show: false, business: null });
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [tempNotes, setTempNotes] = useState('');

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: '', type: 'success' });
    }, 2000);
  };
  const [trainingData, setTrainingData] = useState([]);
  const [partnershipSuggestions, setPartnershipSuggestions] = useState([]);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [trainingForm, setTrainingForm] = useState({
    improved_suggestion: '',
    feedback: ''
  });
  const [chains, setChains] = useState([]);
  const [newChain, setNewChain] = useState({
    name: '',
    category: '',
    aliases: [],
    parent_company: '',
    location_count: null
  });
  const [editingChain, setEditingChain] = useState(null);
  const [chainSearchFilter, setChainSearchFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  
  const { user, logout } = useAuth();

  useEffect(() => {
    loadUserSettings();
    loadSavedSearches();
  }, []);

  useEffect(() => {
    if (activeTab === 'training') {
      fetchTrainingData();
    }
    if (activeTab === 'chains') {
      fetchChainData();
    }
  }, [activeTab]);

  const loadUserSettings = async () => {
    try {
      const response = await axios.get(`${API}/user-settings`);
      if (response.data.settings) {
        setUserSettings(response.data.settings);
        setSearchParams({
          gym_name: response.data.settings.gym_name,
          gym_address: response.data.settings.gym_address,
          radius: response.data.settings.default_radius,
          gym_type: response.data.settings.gym_type,
          demographics: response.data.settings.demographics,
          offers_to_trade: response.data.settings.offers_to_trade,
          partnership_types: response.data.settings.partnership_types,
          partner_categories: response.data.settings.partner_categories,
          excluded_categories: response.data.settings.excluded_categories,
          ignore_chains: response.data.settings.ignore_chains || false
        });
      }
    } catch (error) {
      console.error('Failed to load user settings:', error);
    }
  };

  const loadSavedSearches = async () => {
    try {
      const response = await axios.get(`${API}/saved-searches`);
      setSavedSearches(response.data.searches);
    } catch (error) {
      console.error('Failed to load saved searches:', error);
    }
  };

  const loadSavedSearch = async (searchId) => {
    try {
      const response = await axios.get(`${API}/saved-search/${searchId}`);
      setResults(response.data);
      setActiveTab('results');
    } catch (error) {
      console.error('Failed to load saved search:', error);
    }
  };

  const handleDeleteSearch = (searchId, searchName) => {
    setDeleteModal({ show: true, searchId, searchName });
  };

  const confirmDeleteSearch = async () => {
    if (!deleteModal.searchId) return;
    
    setLoading(true);
    try {
      await axios.delete(`${API}/saved-search/${deleteModal.searchId}`);
      setSavedSearches(prev => prev.filter(search => search.id !== deleteModal.searchId));
      setDeleteModal({ show: false, searchId: null, searchName: '' });
      showToast('Search deleted successfully!', 'success');
    } catch (error) {
      showToast('Failed to delete search', 'error');
    }
    setLoading(false);
  };

  const handleRenameSearch = (searchId, currentName) => {
    setRenameModal({ show: true, searchId, currentName, newName: currentName });
  };

  const confirmRenameSearch = async () => {
    if (!renameModal.searchId || !renameModal.newName.trim()) return;
    
    setLoading(true);
    try {
      await axios.put(`${API}/saved-search/${renameModal.searchId}/rename`, {
        gym_name: renameModal.newName.trim()
      });
      setSavedSearches(prev => prev.map(search => 
        search.id === renameModal.searchId 
          ? { ...search, gym_name: renameModal.newName.trim() }
          : search
      ));
      setRenameModal({ show: false, searchId: null, currentName: '', newName: '' });
      showToast('Search renamed successfully!', 'success');
    } catch (error) {
      showToast('Failed to rename search', 'error');
    }
    setLoading(false);
  };

  // Address autocomplete
  const handleAddressChange = async (value) => {
    setSearchParams(prev => ({ ...prev, gym_address: value }));
    
    if (value.length > 3) {
      try {
        const response = await axios.get(`${API}/autocomplete?input=${encodeURIComponent(value)}`);
        setSuggestions(response.data.predictions || []);
        setShowSuggestions(true);
      } catch (error) {
        console.error('Autocomplete error:', error);
      }
    } else {
      setShowSuggestions(false);
    }
  };

  const handleSuggestionSelect = (suggestion) => {
    setSearchParams(prev => ({ ...prev, gym_address: suggestion.description }));
    setShowSuggestions(false);
  };

  const handleArrayChange = (field, value) => {
    const currentArray = searchParams[field];
    const newArray = currentArray.includes(value)
      ? currentArray.filter(item => item !== value)
      : [...currentArray, value];
    
    setSearchParams(prev => ({ ...prev, [field]: newArray }));
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/search`, searchParams);
      setResults(response.data);
      setActiveTab('results');
      loadSavedSearches(); // Refresh saved searches
    } catch (error) {
      console.error('Search error:', error);
      alert('Search failed: ' + (error.response?.data?.detail || 'Unknown error'));
    }
    
    setLoading(false);
  };

  const handleExportCSV = async () => {
    if (!results) return;
    
    try {
      const response = await axios.get(`${API}/search/export/${results.id}`);
      const { csv_data, filename } = response.data;
      
      // Create and download file
      const blob = new Blob([csv_data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export error:', error);
      alert('Export failed');
    }
  };

  const updateBusinessNotes = async (businessName, notes) => {
    try {
      await axios.put(`${API}/business/${results.id}/${encodeURIComponent(businessName)}/notes`, {
        notes: notes
      });
      
      // Update local state
      setResults(prev => ({
        ...prev,
        businesses: prev.businesses.map(b =>
          b.business_name === businessName ? { ...b, user_notes: notes } : b
        )
      }));
    } catch (error) {
      console.error('Failed to update notes:', error);
      alert('Failed to update notes');
    }
  };

  const updateBusinessStatus = async (businessName, status) => {
    try {
      await axios.put(`${API}/business/${results.id}/${encodeURIComponent(businessName)}/status`, {
        status: status
      });
      
      // Update local state
      setResults(prev => ({
        ...prev,
        businesses: prev.businesses.map(b =>
          b.business_name === businessName ? { ...b, status: status } : b
        )
      }));
    } catch (error) {
      console.error('Failed to update status:', error);
      alert('Failed to update status');
    }
  };

  const toggleBusinessStatus = async (businessName, targetStatus) => {
    const currentBusiness = results.businesses.find(b => b.business_name === businessName);
    const newStatus = currentBusiness?.status === targetStatus ? 'new' : targetStatus;
    
    try {
      await axios.put(`${API}/business/${results.id}/${encodeURIComponent(businessName)}/status`, {
        status: newStatus
      });
      
      // Update local state
      setResults(prev => ({
        ...prev,
        businesses: prev.businesses.map(b =>
          b.business_name === businessName ? { ...b, status: newStatus } : b
        )
      }));
    } catch (error) {
      console.error('Failed to update status:', error);
      alert('Failed to update status');
    }
  };

  const getPartnershipTitle = (suggestion) => {
    if (suggestion.includes('Coupon exchange')) return 'Coupon Exchange Program';
    if (suggestion.includes('Cross-referral')) return 'Cross-Referral Partnership';
    if (suggestion.includes('Professional referral')) return 'Professional Referral Network';
    if (suggestion.includes('Member discount')) return 'Member Discount Program';
    if (suggestion.includes('Corporate wellness')) return 'Corporate Wellness Program';
    if (suggestion.includes('Member perk')) return 'Member Perk Exchange';
    if (suggestion.includes('Health-focused')) return 'Health-Focused Cross-Promotion';
    return 'Partnership Opportunity';
  };

  const saveNotesToBusiness = async () => {
    if (!notesModal.business) return;
    
    try {
      await axios.put(`${API}/business/${results.id}/${encodeURIComponent(notesModal.business.business_name)}/notes`, {
        notes: tempNotes
      });
      
      // Update local state
      setResults(prev => ({
        ...prev,
        businesses: prev.businesses.map(b =>
          b.business_name === notesModal.business.business_name ? { ...b, user_notes: tempNotes } : b
        )
      }));
      
      setNotesModal({ business: null, show: false });
      setTempNotes('');
    } catch (error) {
      console.error('Failed to update notes:', error);
      alert('Failed to update notes');
    }
  };

  const openScoreModal = (business) => {
    setScoreModal({ show: true, business });
  };

  const getScoreExplanation = (business) => {
    const explanations = [];
    let score = 0;
    
    // Base score for having a rating
    if (business.rating) {
      const ratingScore = Math.min(business.rating * 10, 50);
      score += ratingScore;
      explanations.push(`⭐ Rating Score: +${Math.round(ratingScore)} points (${business.rating}/5 stars)`);
    }
    
    // Bonus for review count (popularity indicator)
    const reviewCount = business.review_count || 0;
    let reviewBonus = 0;
    if (reviewCount > 100) {
      reviewBonus = 20;
      explanations.push(`📊 High Reviews: +20 points (${reviewCount} reviews = very popular)`);
    } else if (reviewCount > 50) {
      reviewBonus = 10;
      explanations.push(`📊 Good Reviews: +10 points (${reviewCount} reviews = well-known)`);
    } else if (reviewCount > 10) {
      reviewBonus = 5;
      explanations.push(`📊 Some Reviews: +5 points (${reviewCount} reviews = established)`);
    } else {
      explanations.push(`📊 Few Reviews: +0 points (${reviewCount} reviews = new/unknown)`);
    }
    score += reviewBonus;
    
    // Distance bonus (closer is better)
    const distance = business.distance || 10;
    let distanceBonus = 0;
    if (distance <= 1) {
      distanceBonus = 15;
      explanations.push(`📍 Very Close: +15 points (${distance} miles = walking distance)`);
    } else if (distance <= 3) {
      distanceBonus = 10;
      explanations.push(`📍 Close: +10 points (${distance} miles = short drive)`);
    } else if (distance <= 5) {
      distanceBonus = 5;
      explanations.push(`📍 Nearby: +5 points (${distance} miles = local area)`);
    } else {
      explanations.push(`📍 Far: +0 points (${distance} miles = longer drive)`);
    }
    score += distanceBonus;
    
    // Business type relevance
    if (business.category) {
      const relevantTypes = ['restaurant', 'health', 'spa', 'beauty_salon', 'clothing_store', 
                           'doctor', 'physiotherapist', 'store', 'food', 'meal_takeaway'];
      if (relevantTypes.some(type => business.category.includes(type))) {
        explanations.push(`🎯 Relevant Category: +10 points (${business.category.replace('_', ' ')} = good partnership potential)`);
        score += 10;
      } else {
        explanations.push(`🎯 Other Category: +0 points (${business.category.replace('_', ' ')} = lower partnership potential)`);
      }
    }
    
    const finalScore = Math.min(score, 100);
    
    return {
      score: finalScore,
      explanations,
      summary: finalScore >= 80 ? "Excellent partnership potential! 🔥" : 
               finalScore >= 60 ? "Good partnership opportunity 👍" : 
               "Consider for partnerships ⚡"
    };
  };

  const openNotesModal = (business) => {
    setTempNotes(business.user_notes || '');
    setNotesModal({ business, show: true });
  };

  const refreshSearch = async () => {
    if (!results) return;
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/refresh-search/${results.id}`);
      setResults(prev => ({
        ...prev,
        businesses: response.data.businesses
      }));
      alert(`Search refreshed! Kept ${response.data.kept_count} businesses, found ${response.data.new_count} new ones.`);
    } catch (error) {
      console.error('Refresh error:', error);
      alert('Failed to refresh search');
    }
    setLoading(false);
  };

  const fetchTrainingData = async () => {
    console.log('Fetching training data...');
    try {
      const [trainingRes, suggestionsRes] = await Promise.all([
        axios.get(`${API}/admin/training-data`),
        axios.get(`${API}/admin/partnership-suggestions`)
      ]);
      
      console.log('Training data response:', trainingRes.data);
      console.log('Suggestions response:', suggestionsRes.data);
      
      setTrainingData(trainingRes.data.training_data || []);
      setPartnershipSuggestions(suggestionsRes.data.suggestions || []);
    } catch (error) {
      console.error('Failed to fetch training data:', error);
    }
  };

  const fetchChainData = async () => {
    console.log('Fetching chain data...');
    try {
      const response = await axios.get(`${API}/admin/chains`);
      console.log('Chain data response:', response.data);
      setChains(response.data.chains || []);
    } catch (error) {
      console.error('Failed to fetch chain data:', error);
    }
  };

  const handleSubmitTraining = async () => {
    if (!selectedSuggestion || !trainingForm.improved_suggestion.trim()) {
      alert('Please select a suggestion and provide an improvement');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/admin/train-ai`, {
        business_type: selectedSuggestion.business_type,
        gym_type: selectedSuggestion.gym_type,
        original_suggestion: selectedSuggestion.partnership_suggestion,
        improved_suggestion: trainingForm.improved_suggestion,
        feedback: trainingForm.feedback
      });
      
      alert('Training data submitted successfully!');
      setSelectedSuggestion(null);
      setTrainingForm({ improved_suggestion: '', feedback: '' });
      fetchTrainingData();
    } catch (error) {
      alert('Failed to submit training data');
    }
    setLoading(false);
  };

  const handleCreateChain = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/admin/chains`, newChain);
      setNewChain({
        name: '',
        category: '',
        aliases: [],
        parent_company: '',
        location_count: null
      });
      fetchChainData();
      alert('Chain added successfully!');
    } catch (error) {
      alert('Failed to create chain: ' + (error.response?.data?.detail || 'Unknown error'));
    }
    setLoading(false);
  };

  const handleEditChain = (chain) => {
    setEditingChain({ ...chain });
  };

  const handleUpdateChain = async () => {
    if (!editingChain) return;
    
    setLoading(true);
    try {
      await axios.put(`${API}/admin/chains/${editingChain.id}`, editingChain);
      setEditingChain(null);
      fetchChainData();
      alert('Chain updated successfully!');
    } catch (error) {
      alert('Failed to update chain');
    }
    setLoading(false);
  };

  const handleDeleteChain = async (chainId) => {
    if (!window.confirm('Are you sure you want to delete this chain?')) return;
    
    try {
      await axios.delete(`${API}/admin/chains/${chainId}`);
      fetchChainData();
      alert('Chain deleted successfully!');
    } catch (error) {
      alert('Failed to delete chain');
    }
  };

  const filteredChains = chains.filter(chain => {
    const matchesSearch = chain.name.toLowerCase().includes(chainSearchFilter.toLowerCase());
    const matchesCategory = !categoryFilter || chain.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  // Predefined options
  const studioTypes = ['Boutique Fitness', 'CrossFit Box', 'Traditional Studio', 'Yoga Studio', 'Pilates Studio', 'Martial Arts', '24/7 Fitness', 'Kickboxing Fitness'];
  const partnershipTypes = ['Cross-promotion', 'Referral Program', 'Joint Events', 'Corporate Wellness', 'Bundled Services'];
  const businessCategories = [
    'restaurant', 'spa', 'beauty_salon', 'hair_care', 'clothing_store', 
    'doctor', 'dentist', 'physiotherapist', 'accounting', 'lawyer',
    'real_estate_agency', 'bank', 'insurance_agency', 'car_dealer', 'chiropractor'
  ];
  const tradeOffers = ['Free memberships', 'Personal training sessions', 'Group classes', 'Nutrition consultations'];

  const tabs = [
    { id: 'search', name: 'Find Partners', icon: '🔍' },
    { id: 'results', name: 'Current Results', icon: '📊', disabled: !results },
    { id: 'history', name: 'Saved Searches', icon: '📋' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">🏋️ Studio Partner Finder</h1>
              <p className="text-gray-600">Welcome, {user?.full_name}</p>
            </div>
            <button
              onClick={logout}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition duration-200"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="border-b border-gray-200 mb-8">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                disabled={tab.disabled}
                className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } ${tab.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {tab.icon} {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Search Form */}
        {activeTab === 'search' && (
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Find Strategic Business Partners</h2>
            
            <form onSubmit={handleSearch} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Studio Name</label>
                  <input
                    type="text"
                    value={searchParams.gym_name}
                    onChange={(e) => setSearchParams(prev => ({ ...prev, gym_name: e.target.value }))}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Your studio name"
                  />
                </div>
                
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Studio Address</label>
                  <input
                    type="text"
                    value={searchParams.gym_address}
                    onChange={(e) => handleAddressChange(e.target.value)}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Enter your studio address"
                  />
                  {showSuggestions && suggestions.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {suggestions.map((suggestion, index) => (
                        <div
                          key={index}
                          onClick={() => handleSuggestionSelect(suggestion)}
                          className="px-3 py-2 cursor-pointer hover:bg-gray-100"
                        >
                          {suggestion.description}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Search Radius (miles)</label>
                  <select
                    value={searchParams.radius}
                    onChange={(e) => setSearchParams(prev => ({ ...prev, radius: parseInt(e.target.value) }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value={1}>1 mile</option>
                    <option value={3}>3 miles</option>
                    <option value={5}>5 miles</option>
                    <option value={10}>10 miles</option>
                    <option value={20}>20 miles</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Studio Type</label>
                  <select
                    value={searchParams.gym_type}
                    onChange={(e) => setSearchParams(prev => ({ ...prev, gym_type: e.target.value }))}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value="">Select studio type</option>
                    {studioTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Target Demographics</label>
                <textarea
                  value={searchParams.demographics}
                  onChange={(e) => setSearchParams(prev => ({ ...prev, demographics: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Describe your target demographic (e.g., young professionals, families, seniors)"
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">What Can You Offer?</label>
                  <div className="space-y-2">
                    {tradeOffers.map(offer => (
                      <label key={offer} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={searchParams.offers_to_trade.includes(offer)}
                          onChange={() => handleArrayChange('offers_to_trade', offer)}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">{offer}</span>
                      </label>
                    ))}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Partnership Types</label>
                  <div className="space-y-2">
                    {partnershipTypes.map(type => (
                      <label key={type} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={searchParams.partnership_types.includes(type)}
                          onChange={() => handleArrayChange('partnership_types', type)}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">{type}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Business Categories to Target</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {businessCategories.map(category => (
                    <label key={category} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={searchParams.partner_categories.includes(category)}
                        onChange={() => handleArrayChange('partner_categories', category)}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700 capitalize">{category.replace('_', ' ')}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={searchParams.ignore_chains}
                    onChange={(e) => setSearchParams(prev => ({ ...prev, ignore_chains: e.target.checked }))}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-700">
                    Focus on local businesses only (ignore big chains like McDonald's, Starbucks, CVS, etc.)
                  </span>
                </label>
              </div>
              
              <button
                type="submit"
                disabled={loading || searchParams.partner_categories.length === 0}
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-3 px-4 rounded-lg transition duration-200 disabled:opacity-50"
              >
                {loading ? 'Searching for Partners...' : 'Find Business Partners'}
              </button>
            </form>
          </div>
        )}

        {/* Results */}
        {activeTab === 'results' && results && (
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  Found {results.businesses?.length || 0} Strategic Partners
                </h2>
                <p className="text-gray-600">For {results.gym_name}</p>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={refreshSearch}
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition duration-200 disabled:opacity-50"
                >
                  {loading ? 'Refreshing...' : '🔄 Refresh'}
                </button>
                <button
                  onClick={handleExportCSV}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition duration-200"
                >
                  📄 Export to CSV
                </button>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">Business</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">Contact</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">Partnership Idea</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">Notes</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {results.businesses?.map((business, index) => (
                    <tr key={index} className={`hover:bg-gray-50 ${
                      business.status === 'kept' ? 'bg-green-50' : 
                      business.status === 'removed' ? 'bg-red-50' : ''
                    }`}>
                      <td className="px-4 py-4">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{business.business_name}</div>
                          <div className="text-sm text-gray-500 capitalize">{business.category?.replace('_', ' ')}</div>
                          <div className="text-xs text-gray-400 mt-1">{business.address}</div>
                          <div className="flex items-center space-x-4 mt-2 text-xs text-gray-600">
                            <button
                              onClick={() => openScoreModal(business)}
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium cursor-pointer hover:opacity-80 transition-opacity ${
                                business.score >= 80 ? 'bg-green-100 text-green-800 hover:bg-green-200' :
                                business.score >= 60 ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200' :
                                'bg-red-100 text-red-800 hover:bg-red-200'
                              }`}
                              title="Click to see how this score was calculated"
                            >
                              Score: {Math.round(business.score)}
                            </button>
                            {business.rating && (
                              <span className="flex items-center">
                                <span className="text-yellow-400 mr-1">⭐</span>
                                {business.rating} ({business.review_count || 0})
                              </span>
                            )}
                            <span>{business.distance} mi</span>
                          </div>
                          {business.owner_name && <div className="text-xs text-gray-500 mt-1">Owner: {business.owner_name}</div>}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-900">
                        <div className="space-y-1">
                          {business.phone && <div className="text-xs">📞 {business.phone}</div>}
                          {business.website && (
                            <div>
                              <a href={business.website} target="_blank" rel="noopener noreferrer" 
                                 className="text-indigo-600 hover:text-indigo-500 text-xs">
                                🌐 Website
                              </a>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <button
                          onClick={() => setPartnershipModal({ business, show: true })}
                          className="text-left text-sm text-indigo-600 hover:text-indigo-800 underline cursor-pointer"
                        >
                          {getPartnershipTitle(business.partnership_suggestion)}
                        </button>
                      </td>
                      <td className="px-4 py-4">
                        <div className="space-y-1">
                          <div className="text-xs text-gray-700 line-clamp-2">
                            {business.user_notes ? 
                              (business.user_notes.length > 40 ? 
                                business.user_notes.substring(0, 40) + '...' : 
                                business.user_notes
                              ) : 
                              'No notes yet'
                            }
                          </div>
                          <button
                            onClick={() => openNotesModal(business)}
                            className="text-xs text-indigo-600 hover:text-indigo-500"
                          >
                            {business.user_notes ? 'Edit Notes' : 'Add Notes'}
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex flex-col space-y-2">
                          <button
                            onClick={() => toggleBusinessStatus(business.business_name, 'kept')}
                            className={`text-xs px-3 py-1 rounded transition-colors ${
                              business.status === 'kept' 
                                ? 'bg-green-600 text-white hover:bg-green-500' 
                                : 'bg-green-100 text-green-700 hover:bg-green-200'
                            }`}
                          >
                            ✓ Keep
                          </button>
                          <button
                            onClick={() => toggleBusinessStatus(business.business_name, 'removed')}
                            className={`text-xs px-3 py-1 rounded transition-colors ${
                              business.status === 'removed' 
                                ? 'bg-red-600 text-white hover:bg-red-500' 
                                : 'bg-red-100 text-red-700 hover:bg-red-200'
                            }`}
                          >
                            ✗ Remove
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Saved Searches */}
        {activeTab === 'history' && (
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Saved Searches</h2>
            
            {savedSearches.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500">No saved searches yet. Start by finding some partners!</p>
              </div>
            ) : (
              <div className="space-y-4">
                {savedSearches.map((search) => (
                  <div key={search.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-medium text-gray-900">{search.gym_name}</h3>
                        <p className="text-sm text-gray-600">{search.gym_address}</p>
                        <p className="text-xs text-gray-500">
                          {search.businesses?.length || 0} partners • {new Date(search.updated_at).toLocaleDateString()}
                        </p>
                        <div className="mt-2">
                          <div className="text-xs text-gray-500">
                            Categories: {search.partner_categories?.join(', ')}
                          </div>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => loadSavedSearch(search.id)}
                          className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1 rounded text-sm"
                        >
                          View Results
                        </button>
                        <button
                          onClick={() => handleRenameSearch(search.id, search.gym_name)}
                          className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm"
                        >
                          Rename
                        </button>
                        <button
                          onClick={() => handleDeleteSearch(search.id, search.gym_name)}
                          className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* AI Training Tab */}
        {activeTab === 'training' && (
          <div className="space-y-6">
            {/* Train AI Section */}
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Train AI Partnership Suggestions</h2>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Recent Suggestions */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Partnership Suggestions</h3>
                  <div className="max-h-96 overflow-y-auto space-y-3">
                    {partnershipSuggestions.map((suggestion, index) => (
                      <div 
                        key={index}
                        onClick={() => setSelectedSuggestion(suggestion)}
                        className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                          selectedSuggestion === suggestion 
                            ? 'border-indigo-500 bg-indigo-50' 
                            : 'border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <div className="text-sm font-medium text-gray-900">{suggestion.business_name}</div>
                        <div className="text-xs text-gray-500 mb-2">
                          {suggestion.business_type} × {suggestion.gym_type}
                        </div>
                        <div className="text-sm text-gray-600">{suggestion.partnership_suggestion}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Training Form */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Improve Selected Suggestion</h3>
                  
                  {selectedSuggestion ? (
                    <div className="space-y-4">
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <div className="text-sm font-medium text-gray-900 mb-2">Selected:</div>
                        <div className="text-sm text-gray-600">{selectedSuggestion.partnership_suggestion}</div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Improved Suggestion *</label>
                        <textarea
                          value={trainingForm.improved_suggestion}
                          onChange={(e) => setTrainingForm(prev => ({ ...prev, improved_suggestion: e.target.value }))}
                          rows={4}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                          placeholder="Write a better partnership suggestion..."
                          required
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Feedback (Optional)</label>
                        <textarea
                          value={trainingForm.feedback}
                          onChange={(e) => setTrainingForm(prev => ({ ...prev, feedback: e.target.value }))}
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                          placeholder="Why is this improvement better?"
                        />
                      </div>
                      
                      <button
                        onClick={handleSubmitTraining}
                        disabled={loading || !trainingForm.improved_suggestion.trim()}
                        className="w-full bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg disabled:opacity-50"
                      >
                        {loading ? 'Submitting...' : 'Submit Training'}
                      </button>
                    </div>
                  ) : (
                    <div className="text-gray-500 text-center py-8">
                      Select a partnership suggestion to improve
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Training History */}
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Training History</h2>
              
              {trainingData.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No training data yet</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Business Type</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Studio Type</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Original</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Improved</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {trainingData.map((training, index) => (
                        <tr key={index}>
                          <td className="px-6 py-4 text-sm text-gray-900 capitalize">
                            {training.business_type?.replace('_', ' ')}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">{training.gym_type}</td>
                          <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                            {training.original_suggestion}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                            {training.improved_suggestion}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {new Date(training.created_at).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Chain Management Tab */}
        {activeTab === 'chains' && (
          <div className="space-y-6">
            {/* Add New Chain */}
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Chain Business Management</h2>
              
              <div className="bg-gray-50 p-6 rounded-lg mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Add New Chain Business</h3>
                <form onSubmit={handleCreateChain} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <input
                      type="text"
                      placeholder="Business name (e.g., McDonald's)"
                      value={newChain.name}
                      onChange={(e) => setNewChain(prev => ({ ...prev, name: e.target.value }))}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <select
                      value={newChain.category}
                      onChange={(e) => setNewChain(prev => ({ ...prev, category: e.target.value }))}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">Select category</option>
                      <option value="restaurant">Restaurant</option>
                      <option value="retail">Retail</option>
                      <option value="pharmacy">Pharmacy</option>
                      <option value="gas_station">Gas Station</option>
                      <option value="bank">Bank</option>
                      <option value="auto_service">Auto Service</option>
                      <option value="fitness">Fitness</option>
                      <option value="clothing_store">Clothing</option>
                      <option value="grocery_store">Grocery</option>
                      <option value="electronics">Electronics</option>
                    </select>
                  </div>
                  <div>
                    <input
                      type="text"
                      placeholder="Parent company (optional)"
                      value={newChain.parent_company}
                      onChange={(e) => setNewChain(prev => ({ ...prev, parent_company: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <input
                      type="number"
                      placeholder="Location count (optional)"
                      value={newChain.location_count || ''}
                      onChange={(e) => setNewChain(prev => ({ ...prev, location_count: e.target.value ? parseInt(e.target.value) : null }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <input
                      type="text"
                      placeholder="Aliases (comma-separated, e.g., McDonalds, Mcdonald's)"
                      value={newChain.aliases.join(', ')}
                      onChange={(e) => setNewChain(prev => ({ 
                        ...prev, 
                        aliases: e.target.value.split(',').map(alias => alias.trim()).filter(alias => alias.length > 0)
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <button
                      type="submit"
                      disabled={loading}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg disabled:opacity-50"
                    >
                      {loading ? 'Adding...' : 'Add Chain Business'}
                    </button>
                  </div>
                </form>
              </div>

              {/* Search and Filter */}
              <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <input
                    type="text"
                    placeholder="Search chains..."
                    value={chainSearchFilter}
                    onChange={(e) => setChainSearchFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="">All categories</option>
                    <option value="restaurant">Restaurant</option>
                    <option value="retail">Retail</option>
                    <option value="pharmacy">Pharmacy</option>
                    <option value="gas_station">Gas Station</option>
                    <option value="bank">Bank</option>
                    <option value="auto_service">Auto Service</option>
                    <option value="fitness">Fitness</option>
                  </select>
                </div>
                <div className="text-sm text-gray-600 flex items-center">
                  Total: {filteredChains.length} chains
                </div>
              </div>

              {/* Chains List */}
              {filteredChains.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  {chains.length === 0 ? 'No chains in database' : 'No chains match your search'}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Parent Company</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Locations</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Aliases</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {filteredChains.slice(0, 100).map((chain) => (
                        <tr key={chain.id}>
                          <td className="px-6 py-4 text-sm font-medium text-gray-900">
                            {chain.name}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600 capitalize">
                            {chain.category.replace('_', ' ')}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {chain.parent_company || '-'}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {chain.location_count ? chain.location_count.toLocaleString() : '-'}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                            {chain.aliases?.length > 0 ? chain.aliases.join(', ') : 'None'}
                          </td>
                          <td className="px-6 py-4 text-sm font-medium space-x-2">
                            <button
                              onClick={() => handleEditChain(chain)}
                              className="text-indigo-600 hover:text-indigo-900"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteChain(chain.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filteredChains.length > 100 && (
                    <div className="text-center py-4 text-gray-500">
                      Showing first 100 results. Use search to narrow results.
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Edit Chain Modal */}
            {editingChain && (
              <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-lg shadow-lg rounded-md bg-white">
                  <div className="mt-3">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-medium text-gray-900">Edit Chain Business</h3>
                      <button
                        onClick={() => setEditingChain(null)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Business Name</label>
                        <input
                          type="text"
                          value={editingChain.name}
                          onChange={(e) => setEditingChain(prev => ({ ...prev, name: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                        <select
                          value={editingChain.category}
                          onChange={(e) => setEditingChain(prev => ({ ...prev, category: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        >
                          <option value="restaurant">Restaurant</option>
                          <option value="retail">Retail</option>
                          <option value="pharmacy">Pharmacy</option>
                          <option value="gas_station">Gas Station</option>
                          <option value="bank">Bank</option>
                          <option value="auto_service">Auto Service</option>
                          <option value="fitness">Fitness</option>
                          <option value="clothing_store">Clothing</option>
                          <option value="grocery_store">Grocery</option>
                          <option value="electronics">Electronics</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Parent Company</label>
                        <input
                          type="text"
                          value={editingChain.parent_company || ''}
                          onChange={(e) => setEditingChain(prev => ({ ...prev, parent_company: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Aliases</label>
                        <input
                          type="text"
                          value={editingChain.aliases?.join(', ') || ''}
                          onChange={(e) => setEditingChain(prev => ({ 
                            ...prev, 
                            aliases: e.target.value.split(',').map(alias => alias.trim()).filter(alias => alias.length > 0)
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                    </div>
                    
                    <div className="flex justify-end space-x-3 mt-6">
                      <button
                        onClick={() => setEditingChain(null)}
                        className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-lg text-sm"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleUpdateChain}
                        disabled={loading}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm disabled:opacity-50"
                      >
                        {loading ? 'Updating...' : 'Update Chain'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Partnership Modal */}
        {partnershipModal.show && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-md shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Partnership Opportunity</h3>
                  <button
                    onClick={() => setPartnershipModal({ business: null, show: false })}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <span className="sr-only">Close</span>
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                <div className="mb-4">
                  <h4 className="font-medium text-gray-900">{partnershipModal.business?.business_name}</h4>
                  <p className="text-sm text-gray-500 capitalize">{partnershipModal.business?.category?.replace('_', ' ')}</p>
                </div>
                
                <div className="mb-4">
                  <h5 className="font-medium text-gray-700 mb-2">Suggested Partnership:</h5>
                  <p className="text-sm text-gray-600">{partnershipModal.business?.partnership_suggestion}</p>
                </div>
                
                <div className="mb-4">
                  <h5 className="font-medium text-gray-700 mb-2">Why This Works:</h5>
                  <ul className="text-sm text-gray-600 list-disc list-inside space-y-1">
                    <li>Complementary customer bases</li>
                    <li>Mutual benefit potential</li>
                    <li>Local market synergy</li>
                    <li>Cross-promotional opportunities</li>
                  </ul>
                </div>
                
                <div className="flex justify-end">
                  <button
                    onClick={() => setPartnershipModal({ business: null, show: false })}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm"
                  >
                    Got it!
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Notes Modal */}
        {notesModal.show && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-lg shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Business Notes</h3>
                  <button
                    onClick={() => setNotesModal({ business: null, show: false })}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <span className="sr-only">Close</span>
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                <div className="mb-4">
                  <h4 className="font-medium text-gray-900">{notesModal.business?.business_name}</h4>
                  <p className="text-sm text-gray-500">{notesModal.business?.address}</p>
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Your Notes:</label>
                  <textarea
                    value={tempNotes}
                    onChange={(e) => setTempNotes(e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Add notes about this potential partner..."
                  />
                </div>
                
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setNotesModal({ business: null, show: false })}
                    className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-lg text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveNotesToBusiness}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm"
                  >
                    Save Notes
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="bg-white border-t mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <p className="text-lg font-medium text-gray-900 mb-2">
              Ready to take your studio to the next level?
            </p>
            <p className="text-gray-600 mb-4">
              Easily run ads, automate follow-up, and fill your studio without an agency.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-3 mb-4">
              <a 
                href="https://gymmembermagnet.com/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-block bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-3 px-6 rounded-lg transition duration-200"
              >
                Gym Owners Click Here
              </a>
              <a 
                href="https://studentmagnet.com/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-block bg-purple-600 hover:bg-purple-700 text-white font-medium py-3 px-6 rounded-lg transition duration-200"
              >
                Martial Arts Click Here
              </a>
            </div>
            <p className="text-sm text-gray-500">
              Powered by The Follow Up Ninja™
            </p>
            <p className="text-xs text-gray-400">
              <a href="/privacy-policy" className="hover:text-gray-600 transition-colors">
                Privacy Policy
              </a>
            </p>
          </div>
        </div>
      </footer>

      {/* Score Modal */}
      {scoreModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Score Breakdown</h3>
              <button
                onClick={() => setScoreModal({ show: false, business: null })}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            
            {scoreModal.business && (
              <div className="space-y-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900 mb-1">
                    {Math.round(scoreModal.business.score)}/100
                  </div>
                  <div className="text-sm text-gray-600">
                    {getScoreExplanation(scoreModal.business).summary}
                  </div>
                </div>
                
                <div className="border-t pt-4">
                  <h4 className="font-medium text-gray-900 mb-3">How this score was calculated:</h4>
                  <div className="space-y-2">
                    {getScoreExplanation(scoreModal.business).explanations.map((explanation, index) => (
                      <div key={index} className="text-sm text-gray-600 flex items-start">
                        <span className="mr-2">•</span>
                        <span>{explanation}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500">
                    <strong>Business:</strong> {scoreModal.business.business_name}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

        {/* Delete Confirmation Modal */}
        {deleteModal.show && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Delete Search</h3>
                <button
                  onClick={() => setDeleteModal({ show: false, searchId: null, searchName: '' })}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
              
              <div className="mb-6">
                <p className="text-gray-600">
                  Are you sure you want to delete the search for "{deleteModal.searchName}"? 
                  This action cannot be undone.
                </p>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setDeleteModal({ show: false, searchId: null, searchName: '' })}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteSearch}
                  disabled={loading}
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition duration-200 disabled:opacity-50"
                >
                  {loading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Rename Modal */}
        {renameModal.show && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Rename Search</h3>
                <button
                  onClick={() => setRenameModal({ show: false, searchId: null, currentName: '', newName: '' })}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New name for this search:
                </label>
                <input
                  type="text"
                  value={renameModal.newName}
                  onChange={(e) => setRenameModal(prev => ({ ...prev, newName: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Enter new name"
                  autoFocus
                />
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setRenameModal({ show: false, searchId: null, currentName: '', newName: '' })}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmRenameSearch}
                  disabled={loading || !renameModal.newName.trim()}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg transition duration-200 disabled:opacity-50"
                >
                  {loading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Toast Notification */}
        {toast.show && (
          <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg text-white font-medium shadow-lg transition-all duration-300 ${
            toast.type === 'success' ? 'bg-green-500' : 'bg-red-500'
          }`}>
            {toast.message}
          </div>
        )}
    </div>
  );
};

// Admin Dashboard Component (unchanged from previous version)
const AdminDashboard = () => {
  const [users, setUsers] = useState([]);
  const [searches, setSearches] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [activeTab, setActiveTab] = useState('users');
  const [newAdmin, setNewAdmin] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [trainingData, setTrainingData] = useState([]);
  const [partnershipSuggestions, setPartnershipSuggestions] = useState([]);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [trainingForm, setTrainingForm] = useState({
    improved_suggestion: '',
    feedback: ''
  });
  
  // Chain management states
  const [chains, setChains] = useState([]);
  const [newChain, setNewChain] = useState({
    name: '',
    category: '',
    aliases: [],
    parent_company: '',
    location_count: null
  });
  const [editingChain, setEditingChain] = useState(null);
  const [chainSearchFilter, setChainSearchFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  
  // Search view modal state
  const [viewingSearch, setViewingSearch] = useState(null);
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [scoreModal, setScoreModal] = useState({ show: false, business: null });
  
  const { logout } = useAuth();

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (activeTab === 'training') {
      fetchTrainingData();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'chains') {
      fetchChainData();
    }
  }, [activeTab]);

  const fetchData = async () => {
    try {
      const [usersRes, searchesRes, adminsRes] = await Promise.all([
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/searches`),
        axios.get(`${API}/admin/list`)
      ]);
      
      setUsers(usersRes.data.users);
      setSearches(searchesRes.data.searches);
      setAdmins(adminsRes.data.admins);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
    }
  };

  const fetchTrainingData = async () => {
    console.log('Fetching training data...');
    try {
      const [trainingRes, suggestionsRes] = await Promise.all([
        axios.get(`${API}/admin/training-data`),
        axios.get(`${API}/admin/partnership-suggestions`)
      ]);
      
      console.log('Training data response:', trainingRes.data);
      console.log('Suggestions response:', suggestionsRes.data);
      
      setTrainingData(trainingRes.data.training_data || []);
      setPartnershipSuggestions(suggestionsRes.data.suggestions || []);
    } catch (error) {
      console.error('Failed to fetch training data:', error);
    }
  };

  const fetchChainData = async () => {
    console.log('Fetching chain data...');
    try {
      const response = await axios.get(`${API}/admin/chains`);
      console.log('Chain data response:', response.data);
      setChains(response.data.chains || []);
    } catch (error) {
      console.error('Failed to fetch chain data:', error);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      setUsers(users.filter(user => user.id !== userId));
    } catch (error) {
      alert('Failed to delete user');
    }
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/admin/create`, newAdmin);
      setNewAdmin({ email: '', password: '' });
      fetchData();
      alert('Admin created successfully');
    } catch (error) {
      alert('Failed to create admin: ' + (error.response?.data?.detail || 'Unknown error'));
    }
    
    setLoading(false);
  };

  const handleDeleteAdmin = async (adminId) => {
    if (!window.confirm('Are you sure you want to delete this admin?')) return;
    
    try {
      await axios.delete(`${API}/admin/${adminId}`);
      setAdmins(admins.filter(admin => admin.id !== adminId));
    } catch (error) {
      alert('Failed to delete admin: ' + (error.response?.data?.detail || 'Cannot delete super admin'));
    }
  };

  const handleViewSearch = async (searchId) => {
    try {
      const response = await axios.get(`${API}/admin/search/${searchId}`);
      setViewingSearch(response.data);
      setSearchModalOpen(true);
    } catch (error) {
      console.error('Failed to fetch search details:', error);
      alert('Failed to load search details');
    }
  };

  const openScoreModal = (business) => {
    setScoreModal({ show: true, business });
  };

  const getScoreExplanation = (business) => {
    const explanations = [];
    let score = 0;
    
    // Base score for having a rating
    if (business.rating) {
      const ratingScore = Math.min(business.rating * 10, 50);
      score += ratingScore;
      explanations.push(`⭐ Rating Score: +${Math.round(ratingScore)} points (${business.rating}/5 stars)`);
    }
    
    // Bonus for review count (popularity indicator)
    const reviewCount = business.review_count || 0;
    let reviewBonus = 0;
    if (reviewCount > 100) {
      reviewBonus = 20;
      explanations.push(`📊 High Reviews: +20 points (${reviewCount} reviews = very popular)`);
    } else if (reviewCount > 50) {
      reviewBonus = 10;
      explanations.push(`📊 Good Reviews: +10 points (${reviewCount} reviews = well-known)`);
    } else if (reviewCount > 10) {
      reviewBonus = 5;
      explanations.push(`📊 Some Reviews: +5 points (${reviewCount} reviews = established)`);
    } else {
      explanations.push(`📊 Few Reviews: +0 points (${reviewCount} reviews = new/unknown)`);
    }
    score += reviewBonus;
    
    // Distance bonus (closer is better)
    const distance = business.distance || 10;
    let distanceBonus = 0;
    if (distance <= 1) {
      distanceBonus = 15;
      explanations.push(`📍 Very Close: +15 points (${distance} miles = walking distance)`);
    } else if (distance <= 3) {
      distanceBonus = 10;
      explanations.push(`📍 Close: +10 points (${distance} miles = short drive)`);
    } else if (distance <= 5) {
      distanceBonus = 5;
      explanations.push(`📍 Nearby: +5 points (${distance} miles = local area)`);
    } else {
      explanations.push(`📍 Far: +0 points (${distance} miles = longer drive)`);
    }
    score += distanceBonus;
    
    // Business type relevance
    if (business.category) {
      const relevantTypes = ['restaurant', 'health', 'spa', 'beauty_salon', 'clothing_store', 
                           'doctor', 'physiotherapist', 'store', 'food', 'meal_takeaway'];
      if (relevantTypes.some(type => business.category.includes(type))) {
        explanations.push(`🎯 Relevant Category: +10 points (${business.category.replace('_', ' ')} = good partnership potential)`);
        score += 10;
      } else {
        explanations.push(`🎯 Other Category: +0 points (${business.category.replace('_', ' ')} = lower partnership potential)`);
      }
    }
    
    const finalScore = Math.min(score, 100);
    
    return {
      score: finalScore,
      explanations,
      summary: finalScore >= 80 ? "Excellent partnership potential! 🔥" : 
               finalScore >= 60 ? "Good partnership opportunity 👍" : 
               "Consider for partnerships ⚡"
    };
  };

  const handleExportUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users/export`);
      const { csv_data, filename } = response.data;
      
      const blob = new Blob([csv_data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert('Export failed');
    }
  };

  const handleSubmitTraining = async () => {
    if (!selectedSuggestion || !trainingForm.improved_suggestion.trim()) {
      alert('Please select a suggestion and provide an improvement');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/admin/train-ai`, {
        business_type: selectedSuggestion.business_type,
        gym_type: selectedSuggestion.gym_type,
        original_suggestion: selectedSuggestion.partnership_suggestion,
        improved_suggestion: trainingForm.improved_suggestion,
        feedback: trainingForm.feedback
      });
      
      alert('Training data submitted successfully!');
      setSelectedSuggestion(null);
      setTrainingForm({ improved_suggestion: '', feedback: '' });
      fetchTrainingData();
    } catch (error) {
      alert('Failed to submit training data');
    }
    setLoading(false);
  };

  const tabs = [
    { id: 'users', name: 'Users', count: users.length },
    { id: 'searches', name: 'Searches', count: searches.length },
    { id: 'admins', name: 'Admins', count: admins.length },
    { id: 'training', name: 'AI Training', count: trainingData.length },
    { id: 'chains', name: 'Chain Management', count: chains.length }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">🔐 Admin Dashboard</h1>
              <p className="text-gray-600">Gym Partner Finder Management</p>
            </div>
            <button
              onClick={logout}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="border-b border-gray-200 mb-8">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.name} ({tab.count})
              </button>
            ))}
          </nav>
        </div>

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">User Management</h2>
              <button
                onClick={handleExportUsers}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg"
              >
                📄 Export Users CSV
              </button>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Consents</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {users.map((user) => (
                    <tr key={user.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {user.full_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{user.email}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{user.phone}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(user.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex space-x-2">
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            user.email_consent ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            Email: {user.email_consent ? 'Yes' : 'No'}
                          </span>
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            user.sms_consent ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            SMS: {user.sms_consent ? 'Yes' : 'No'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleDeleteUser(user.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Searches Tab */}
        {activeTab === 'searches' && (
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Search Analytics</h2>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Gym</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Results</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {searches.map((search) => (
                    <tr key={search.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {users.find(u => u.id === search.user_id)?.full_name || 'Unknown'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{search.gym_name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {search.gym_address?.split(',')[0]}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {search.businesses?.length || 0} partners
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(search.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <button
                          onClick={() => handleViewSearch(search.id)}
                          className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1 rounded text-sm"
                        >
                          View Search
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Admins Tab */}
        {activeTab === 'admins' && (
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Admin Management</h2>
            
            {/* Create Admin Form */}
            <div className="bg-gray-50 p-6 rounded-lg mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Create New Admin</h3>
              <form onSubmit={handleCreateAdmin} className="flex space-x-4">
                <input
                  type="email"
                  placeholder="Admin email"
                  value={newAdmin.email}
                  onChange={(e) => setNewAdmin(prev => ({ ...prev, email: e.target.value }))}
                  required
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
                <input
                  type="password"
                  placeholder="Password (min 8 chars)"
                  value={newAdmin.password}
                  onChange={(e) => setNewAdmin(prev => ({ ...prev, password: e.target.value }))}
                  required
                  minLength={8}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg disabled:opacity-50"
                >
                  {loading ? 'Creating...' : 'Create Admin'}
                </button>
              </form>
            </div>
            
            {/* Admins List */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {admins.map((admin) => (
                    <tr key={admin.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {admin.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(admin.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          admin.is_super_admin 
                            ? 'bg-purple-100 text-purple-800' 
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {admin.is_super_admin ? 'Super Admin' : 'Admin'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {!admin.is_super_admin && (
                          <button
                            onClick={() => handleDeleteAdmin(admin.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Delete
                          </button>
                        )}
                        {admin.is_super_admin && (
                          <span className="text-gray-400">Protected</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      
      {/* Search View Modal */}
      {searchModalOpen && viewingSearch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-6xl max-h-[90vh] overflow-y-auto m-4">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Search Details</h2>
                <p className="text-gray-600">User: {viewingSearch.user?.full_name} ({viewingSearch.user?.email})</p>
                <p className="text-gray-600">Gym: {viewingSearch.search?.gym_name}</p>
              </div>
              <button
                onClick={() => setSearchModalOpen(false)}
                className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-lg"
              >
                Close
              </button>
            </div>
            
            {/* Search Results Table */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Business</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Partnership Idea</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Notes</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {viewingSearch.search?.businesses?.map((business, index) => (
                    <tr key={index} className={`hover:bg-gray-50 ${
                      business.status === 'kept' ? 'bg-green-50' : 
                      business.status === 'removed' ? 'bg-red-50' : ''
                    }`}>
                      <td className="px-4 py-4">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{business.business_name}</div>
                          <div className="text-sm text-gray-500 capitalize">{business.category?.replace('_', ' ')}</div>
                          <div className="text-xs text-gray-400 mt-1">{business.address}</div>
                          <div className="flex items-center space-x-4 mt-2 text-xs text-gray-600">
                            <button
                              onClick={() => openScoreModal(business)}
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium cursor-pointer hover:opacity-80 transition-opacity ${
                                business.score >= 80 ? 'bg-green-100 text-green-800 hover:bg-green-200' :
                                business.score >= 60 ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200' :
                                'bg-red-100 text-red-800 hover:bg-red-200'
                              }`}
                              title="Click to see how this score was calculated"
                            >
                              Score: {Math.round(business.score)}
                            </button>
                            {business.rating && (
                              <span className="flex items-center">
                                <span className="text-yellow-400 mr-1">⭐</span>
                                {business.rating} ({business.review_count || 0})
                              </span>
                            )}
                            <span>{business.distance} mi</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-900">
                        <div className="space-y-1">
                          {business.phone && <div className="text-xs">📞 {business.phone}</div>}
                          {business.website && (
                            <div>
                              <a href={business.website} target="_blank" rel="noopener noreferrer" 
                                 className="text-indigo-600 hover:text-indigo-500 text-xs">
                                🌐 Website
                              </a>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-sm text-gray-600">
                          {business.partnership_suggestion}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-xs text-gray-700">
                          {business.user_notes || 'No notes'}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          business.status === 'kept' ? 'bg-green-100 text-green-800' :
                          business.status === 'removed' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {business.status || 'new'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Score Modal */}
      {scoreModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Score Breakdown</h3>
              <button
                onClick={() => setScoreModal({ show: false, business: null })}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            
            {scoreModal.business && (
              <div className="space-y-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900 mb-1">
                    {Math.round(scoreModal.business.score)}/100
                  </div>
                  <div className="text-sm text-gray-600">
                    {getScoreExplanation(scoreModal.business).summary}
                  </div>
                </div>
                
                <div className="border-t pt-4">
                  <h4 className="font-medium text-gray-900 mb-3">How this score was calculated:</h4>
                  <div className="space-y-2">
                    {getScoreExplanation(scoreModal.business).explanations.map((explanation, index) => (
                      <div key={index} className="text-sm text-gray-600 flex items-start">
                        <span className="mr-2">•</span>
                        <span>{explanation}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500">
                    <strong>Business:</strong> {scoreModal.business.business_name}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { user, admin, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (requireAdmin) {
    return admin ? children : <Navigate to="/" replace />;
  }
  
  return user ? children : <Navigate to="/" replace />;
};

// Main App Component
function App() {
  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <Routes>
            <Route path="/" element={
              <AuthProtectedRoute>
                <Login />
              </AuthProtectedRoute>
            } />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } />
            <Route path="/admin-login" element={
              <AuthProtectedRoute>
                <AdminLogin />
              </AuthProtectedRoute>
            } />
            <Route path="/admin" element={
              <ProtectedRoute requireAdmin={true}>
                <AdminDashboard />
              </ProtectedRoute>
            } />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          </Routes>
        </div>
      </Router>
    </AuthProvider>
  );
}

// Component to redirect authenticated users
const AuthProtectedRoute = ({ children }) => {
  const { user, admin, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
      </div>
    );
  }
  
  if (admin) return <Navigate to="/admin" replace />;
  if (user) return <Navigate to="/dashboard" replace />;
  
  return children;
};

// Privacy Policy Component
const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-lg p-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
          <p className="text-gray-600">Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
        </div>

        <div className="prose prose-lg max-w-none space-y-8">
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Information We Collect</h2>
            <p className="text-gray-700 mb-4">
              The Follow Up Ninja ("we," "our," or "us") collects information you provide directly to us, such as when you create an account, use our services, or contact us for support.
            </p>
            <p className="text-gray-700">
              <strong>Information we collect includes:</strong>
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Personal information (name, email address, phone number)</li>
              <li>Business information (gym name, address, business type)</li>
              <li>Usage data and analytics</li>
              <li>Communication preferences</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. How We Use Your Information</h2>
            <p className="text-gray-700 mb-4">We use the information we collect to:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Provide, maintain, and improve our services</li>
              <li>Process transactions and send related information</li>
              <li>Send promotional communications (with your consent)</li>
              <li>Respond to your comments, questions, and requests</li>
              <li>Monitor and analyze trends, usage, and activities</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Information Sharing and Disclosure</h2>
            <p className="text-gray-700 mb-4">
              We do not sell, trade, or otherwise transfer your personal information to third parties without your consent, except as described in this policy.
            </p>
            <p className="text-gray-700">
              <strong>We may share information:</strong>
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>With service providers who assist us in operating our services</li>
              <li>To comply with legal obligations or protect our rights</li>
              <li>In connection with a business transfer or sale</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Marketing Communications</h2>
            <p className="text-gray-700 mb-4">
              With your consent, we may send you promotional emails, text messages, and make phone calls regarding our services, business growth tips, and partnership opportunities.
            </p>
            <p className="text-gray-700 mb-4">
              <strong>Your rights regarding marketing communications:</strong>
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>You can opt out of text messages by replying STOP</li>
              <li>You can unsubscribe from emails using the link in each email</li>
              <li>You can request to stop phone calls by contacting us directly</li>
              <li>Consent is not required as a condition of purchase</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Data Security</h2>
            <p className="text-gray-700">
              We implement appropriate security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet is 100% secure.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Your Rights</h2>
            <p className="text-gray-700 mb-4">You have the right to:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Access, update, or delete your personal information</li>
              <li>Opt out of marketing communications</li>
              <li>Request a copy of your data</li>
              <li>File a complaint with relevant authorities</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Changes to This Policy</h2>
            <p className="text-gray-700">
              We may update this privacy policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last updated" date.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Contact Us</h2>
            <div className="bg-gray-50 p-6 rounded-lg">
              <p className="text-gray-700 mb-2">
                If you have any questions about this Privacy Policy, please contact us:
              </p>
              <div className="text-gray-700">
                <p><strong>The Follow Up Ninja</strong></p>
                <p>1309 COFFEEN AVE STE 1200</p>
                <p>SHERIDAN, WY 82801</p>
                <p>Email: <a href="mailto:jon@thefollowupninja.com" className="text-indigo-600 hover:text-indigo-500">jon@thefollowupninja.com</a></p>
              </div>
            </div>
          </section>
        </div>

        <div className="mt-12 pt-8 border-t text-center">
          <button
            onClick={() => window.history.back()}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded-lg transition duration-200"
          >
            Go Back
          </button>
        </div>
      </div>
    </div>
  );
};

export default App;